import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Row,
  Col,
  HeaderMenu,
  usePub,
  useSub,
} from "@neumetric/blueprint-lib/components";
import {
  getNewContent,
  getNewButtonLabel,
  MainTabs,
  getMenuItems,
} from "./helper";
import { ImportPopup } from "./ImportPopup";
import "./style.scss";
import { useDispatch } from "react-redux";
import { mitigatorActions } from "../../../store";

export const MitigatorListHomeScreen = (props) => {
  const navigate = useNavigate();
  const publish = usePub();
  const location = useLocation();
  const dispatch = useDispatch();
  const pathParts = location.pathname.split("/").filter(Boolean);
  const lastPart = pathParts[pathParts.length - 1];
  const initialTab = lastPart === "L" ? "S" : lastPart;
  const initialSubmenu =
    new URLSearchParams(location.search).get("tab") || "Active";
  const [state, setState] = React.useState({
    showNewContent: false,
    activeTab: initialTab,
    activeSubmenu: initialSubmenu,
    showImportPopup: false,
    importKey: "import-source",
  });
  const activeTabRef = React.useRef(state.activeTab);
  const [selectedThreatIds, setSelectedThreatIds] = React.useState([]);
  const [selectedVulnerabilityIds, setSelectedVulnerabilityIds] =
    React.useState([]);

  useSub("MITIGATOR_SELECTED_THREAT_IDS", (data) => {
    setSelectedThreatIds(data.ids || []);
  });
  useSub("MITIGATOR_SELECTED_VULNERABILITY_IDS", (data) => {
    setSelectedVulnerabilityIds(data.ids || []);
  });
  React.useEffect(() => {
    dispatch(mitigatorActions.fetchRisks());
  }, []);

  React.useEffect(() => {
    const p = new URLSearchParams(location.search);
    const shouldOpen = p.get("open") === "view-panel";
    const tabParam = p.get("tab") || "active";

    setState((prev) => ({
      ...prev,
      showNewContent: shouldOpen,
      activeSubmenu: tabParam,
    }));
  }, [location.search]);

  const updateState = (newState) => {
    setState((prev) => ({ ...prev, ...newState }));
  };

  const cleanParams = () => {
    const p = new URLSearchParams(window.location.search);
    p.delete("_ts"); // <---- remove timestamp
    p.delete("open");
    p.delete("id");
    return p;
  };

  const handleTabChange = React.useCallback(
    (tab, submenu) => {
      let p = cleanParams(); // <---- clean URL params
      if (submenu) p.set("tab", submenu);

      updateState({
        activeTab: tab,
        activeSubmenu: submenu,
        showNewContent: false,
      });
      publish("MITIGATOR_SELECTED_THREAT_IDS", { ids: [] });
      publish("MITIGATOR_SELECTED_VULNERABILITY_IDS", { ids: [] });
      publish("MITIGATOR_CLEAR_TABLE_SELECTION", {});
      setSelectedThreatIds([]);
      setSelectedVulnerabilityIds([]);

      navigate(`/M/L/${tab}?${p.toString()}`, { replace: true });
    },
    [navigate],
  );

  const handleSubmenuClick = React.useCallback(
    (submenu) => {
      let p = cleanParams(); // <---- clean URL params
      p.set("tab", submenu);

      updateState({
        activeSubmenu: submenu,
        showNewContent: false,
      });
      publish("MITIGATOR_SELECTED_THREAT_IDS", { ids: [] });
      publish("MITIGATOR_SELECTED_VULNERABILITY_IDS", { ids: [] });
      publish("MITIGATOR_CLEAR_TABLE_SELECTION", {});
      setSelectedThreatIds([]);
      setSelectedVulnerabilityIds([]);

      // window.history.replaceState(
      //   {},
      //   "",
      //   `/M/L/${state.activeTab}?${p.toString()}`
      // );
      navigate(`${location.pathname}?${p.toString()}`, { replace: true });

      publish("MITIGATOR_SUBMENU_TAB", { tab: submenu });
    },
    [state.activeTab, publish],
  );

  const handleNewObjectClick = React.useCallback(() => {
    updateState({ showNewContent: true });
  }, []);

  const getNewButton = React.useCallback(() => {
    // hide while Create panel is visible
    if (state.showNewContent) return null;
    if (state.activeTab === "R") return null;
    return {
      label: getNewButtonLabel(state.activeTab, state.activeSubmenu),
      icon: "ADD",
      onClick: handleNewObjectClick,
    };
  }, [state.showNewContent, state.activeTab, state.activeSubmenu]);

  useSub("MITIGATOR_SHOW_NEW_CONTENT", (payload) => {
    updateState({ showNewContent: true });
  });

  React.useEffect(() => {
    activeTabRef.current = state.activeTab;
  }, [state.activeTab]);

  useSub("MITIGATOR_HIDE_NEW_CONTENT", () => {
    setState((prev) => ({ ...prev, showNewContent: false }));
    const p = cleanParams();
    const tab = activeTabRef.current;
    if (state.activeSubmenu) {
      p.set("tab", state.activeSubmenu);
    }
    navigate(`/M/L/${tab}?${p.toString()}`, { replace: true });
  });

  // const handleMenuClick = (activeTab, activeSubmenu, item) => {
  //   const key = item?.id || item?.key || item;
  //   if (key) {
  //     updateState({ importKey: key, showImportPopup: true });
  //   }
  // };

  const handleMenuClick = (item) => {
    const key = item?.id;

    // IMPORT actions (existing)
    if (key?.startsWith("import")) {
      updateState({ importKey: key, showImportPopup: true });
      return;
    }

    // ---- Bulk Actions ----
    if (key === "bulk-archive") {
      dispatch(
        mitigatorActions.bulkUpdateThreatStatus({
          threat_ids: selectedThreatIds,
          updates: { status: "archived" },
        }),
      );
      publish("MITIGATOR_SELECTED_THREAT_IDS", { ids: [] });
      publish("MITIGATOR_CLEAR_TABLE_SELECTION", {});
      setSelectedThreatIds([]);

      return;
    }

    if (key === "bulk-activate") {
      dispatch(
        mitigatorActions.bulkUpdateThreatStatus({
          threat_ids: selectedThreatIds,
          updates: { status: "active" },
        }),
      );
      publish("MITIGATOR_SELECTED_THREAT_IDS", { ids: [] });
      publish("MITIGATOR_CLEAR_TABLE_SELECTION", {});
      setSelectedThreatIds([]);
      return;
    }
    if (key === "bulk-archive-vulnerability") {
      dispatch(
        mitigatorActions.bulkUpdateVulnerabilityStatus({
          vulnerability_ids: selectedVulnerabilityIds,
          updates: { status: "archived" },
        }),
      );

      publish("MITIGATOR_SELECTED_VULNERABILITY_IDS", { ids: [] });
      publish("MITIGATOR_CLEAR_TABLE_SELECTION", {});
      setSelectedVulnerabilityIds([]);
      return;
    }

    if (key === "bulk-activate-vulnerability") {
      dispatch(
        mitigatorActions.bulkUpdateVulnerabilityStatus({
          vulnerability_ids: selectedVulnerabilityIds,
          updates: { status: "active" },
        }),
      );

      publish("MITIGATOR_SELECTED_VULNERABILITY_IDS", { ids: [] });
      publish("MITIGATOR_CLEAR_TABLE_SELECTION", {});
      setSelectedVulnerabilityIds([]);
      return;
    }
  };

  return (
    <Row
      className="w-100 h-100 overflow-hide pos-relative screen-pad exo2-all"
      align="start"
    >
      <Col className="flex-auto h-100 overflow-hidden">
        <HeaderMenu
          initialTab={state.activeTab}
          initialSubmenu={state.activeSubmenu}
          tabItems={MainTabs}
          classes={{ root: "pr-20 pt-20" }}
          onTabChange={handleTabChange}
          onSubmenuClick={handleSubmenuClick}
          newObject={getNewButton()}
          menu={
            state.showNewContent || state.activeTab === "R"
              ? null
              : getMenuItems(
                state.activeTab,
                state.activeSubmenu,
                handleMenuClick,
                state.activeTab === "T"
                  ? selectedThreatIds
                  : state.activeTab === "V"
                    ? selectedVulnerabilityIds
                    : [],
              )
          }
        />

        <Col
          className="w-100 flex-auto overflow-hide mt-16 mb-16"
          style={{ minHeight: 0 }}
        >
          {props.children}
        </Col>
      </Col>
      <Col
        className={`mitigator-new-content h-100 bg-table-highlight overflow-hide ${state.showNewContent ? "open" : ""
          }`}
      >
        {state.showNewContent && (
          <React.Fragment>
            {getNewContent(state.activeTab, state.activeSubmenu)}
          </React.Fragment>
        )}
      </Col>
      <ImportPopup
        open={state.showImportPopup}
        onClose={() => updateState({ showImportPopup: false })}
        menuKey={state.importKey}
      />
    </Row>
  );
};
